import { numFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Typography, Skeleton, Button, Tooltip } from '@mui/material';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import { ownCalendarOnly } from '../../utils/access';
import useCalendarState from './hooks/useCalendarState';
import { ALL_WEEK_DAYS } from './calendarConstants';
import { weeksOf, employeesById, workdaysOf } from './hooks/monthUtils';
import { parseDateKey, format } from './hooks/dateUtils';
import MonthSummaryStrip from './MonthSummaryStrip';
import MonthDayCell from './MonthDayCell';
import MonthLaneGrid from './MonthLaneGrid';
import MonthDayPeek from './MonthDayPeek';

const WEEK_COLUMN = 44;

/**
 * Month mode: the summary strip, the six-week grid (or the mechanic lanes) and the day peek.
 * Reads the payload of /api/scheduling/calendar/month/ from the store; MainCalendarView
 * fetches it.
 */
function CalendarMonthGrid({ t, isShortScreen }) {
  const {
    monthData,
    currentView,
    selectedMonthDay,
    setSelectedMonthDay,
    switchToDay,
    setCurrentDate,
    setCalendarMode,
    schedulingModeTarget,
    exitSchedulingMode,
    openScheduleDialogForDay,
    monthOpenHours,
    setMonthOpenHours,
  } = useCalendarState();
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const navigate = useNavigate();
  const ownOnly = ownCalendarOnly(currentOrganization);

  const employees = React.useMemo(() => employeesById(monthData), [monthData]);
  const weeks = React.useMemo(() => (monthData ? weeksOf(monthData.days) : []), [monthData]);
  // Column headers follow the workshop's own open weekdays (a Saturday-open workshop is not a
  // Monday-to-Friday one, and a week may have a gap).
  const workdays = React.useMemo(() => workdaysOf(monthData?.work_schedule), [monthData]);

  const handleSelectDay = React.useCallback(
    (day) => {
      if (schedulingModeTarget) {
        // Scheduling mode: the day is the answer; the dialog asks for the time.
        openScheduleDialogForDay(day.date, schedulingModeTarget);
        exitSchedulingMode();
        return;
      }
      setSelectedMonthDay(day.date);
    },
    [schedulingModeTarget, openScheduleDialogForDay, exitSchedulingMode, setSelectedMonthDay]
  );

  const handleOpenDay = React.useCallback(
    (day) => {
      setSelectedMonthDay(day.date);
      switchToDay(parseDateKey(day.date));
    },
    [setSelectedMonthDay, switchToDay]
  );

  const handleOpenWeek = React.useCallback(
    (week) => {
      // Anchor the week view on the clicked week's Monday
      setSelectedMonthDay(week.days[0].date);
      setCalendarMode('week');
    },
    [setSelectedMonthDay, setCalendarMode]
  );

  const handleJumpToNextFreeDay = React.useCallback(() => {
    if (!monthData?.next_free_day) return;
    const target = parseDateKey(monthData.next_free_day);
    setCurrentDate(target);
    setSelectedMonthDay(format(target, 'yyyy-MM-dd'));
  }, [monthData, setCurrentDate, setSelectedMonthDay]);

  if (!monthData) {
    return (
      <Box sx={{ flex: 1, p: 2 }}>
        <Skeleton variant="rectangular" height={72} sx={{ borderRadius: '10px', mb: 2 }} />
        <Skeleton variant="rectangular" height="70%" sx={{ borderRadius: '12px' }} />
      </Box>
    );
  }

  const showParts = Boolean(monthData.parts_tracking_enabled);
  const showBookings = Boolean(currentOrganization?.booking_beta);
  const noEmployees = (monthData.employees || []).length === 0;
  // Rows share the height that is left under the summary strip, so a whole month fits a
  // normal screen without scrolling. The floor lives on the cells, and a row is never
  // shorter than its tallest cell's content, so a chip under the status dots is not
  // clipped when the grid is tight; the grid scrolls instead.
  const cellMinHeight = isShortScreen ? 84 : 100;

  return (
    <Box sx={{ flex: 1, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
      <Box
        data-testid="calendar-month-grid"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          p: isShortScreen ? '10px 16px 12px' : '16px 20px 16px',
        }}
      >
        <MonthSummaryStrip
          monthData={monthData}
          compact={isShortScreen}
          openHours={monthOpenHours}
          onOpenHoursChange={setMonthOpenHours}
          onJumpToNextFreeDay={handleJumpToNextFreeDay}
        />

        {noEmployees && (
          <Box
            data-testid="calendar-month-no-employees"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5, px: 1.75, py: 1.25, borderRadius: '10px', bgcolor: '#fef3c7', color: '#92400e', fontSize: '13px' }}
          >
            {t('calendar.month.noEmployeesHint')}
            {!ownOnly && (
              <Button size="small" onClick={() => navigate('/employees')} sx={{ textTransform: 'none', color: '#92400e', fontWeight: 600 }}>
                {t('Employees')}
              </Button>
            )}
          </Box>
        )}

        {/* A user who sees only their own row is locked to the employee view in the week
            and day views (one row either way). In the month that lock would give them a
            single lane of bars and never the grid with the summary, the "does N h fit"
            field and the next free day, which is what the month is for. */}
        {(currentView === 'employee' || currentView === 'bay') && !ownOnly ? (
          <MonthLaneGrid monthData={monthData} selectedDate={selectedMonthDay} onSelect={handleSelectDay} mode={currentView} />
        ) : (
          <Box
            sx={{
              flex: '1 0 auto',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            {/* weekday header */}
            <Box sx={{ flexShrink: 0, display: 'grid', gridTemplateColumns: `${WEEK_COLUMN}px repeat(7, minmax(0, 1fr))`, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
              <Typography component="div" sx={{ py: 1.25, textAlign: 'center', fontSize: '10px', fontWeight: 700, color: 'text.disabled', letterSpacing: '0.06em' }}>
                {t('calendar.month.weekColumn')}
              </Typography>
              {ALL_WEEK_DAYS.map((weekday) => (
                <Typography
                  key={weekday.index}
                  component="div"
                  sx={{ py: 1.25, textAlign: 'center', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: workdays.has(weekday.index) ? 'text.secondary' : 'text.disabled', borderLeft: 1, borderColor: 'divider' }}
                >
                  {t(weekday.shortKey)}
                </Typography>
              ))}
            </Box>

            {weeks.map((week) => (
              <Box
                key={week.days[0].date}
                sx={{ flex: '1 1 0', display: 'grid', gridTemplateColumns: `${WEEK_COLUMN}px repeat(7, minmax(0, 1fr))` }}
              >
                <Tooltip title={t('calendar.month.openWeek', { week: week.number })} placement="right" enterDelay={600}>
                  <Box
                    data-testid={`month-week-${week.number}`}
                    onClick={() => handleOpenWeek(week)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#fbfcfd',
                      borderRight: 1,
                      borderBottom: 1,
                      borderColor: '#edf2f7',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: 'text.disabled',
                      ...numFont,
                      cursor: 'pointer',
                      '&:hover': { color: 'secondary.main', bgcolor: 'background.default' },
                    }}
                  >
                    {week.number}
                  </Box>
                </Tooltip>
                {week.days.map((day) => (
                  <MonthDayCell
                    key={day.date}
                    day={day}
                    employeesById={employees}
                    selected={selectedMonthDay === day.date}
                    isShortScreen={isShortScreen}
                    minHeight={cellMinHeight}
                    showParts={showParts}
                    showBookings={showBookings}
                    schedulingMode={Boolean(schedulingModeTarget)}
                    onSelect={handleSelectDay}
                    onOpenDay={handleOpenDay}
                  />
                ))}
              </Box>
            ))}
          </Box>
        )}

      </Box>

      {selectedMonthDay && !schedulingModeTarget && <MonthDayPeek isShortScreen={isShortScreen} />}
    </Box>
  );
}

export default withTranslation()(CalendarMonthGrid);
